import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PickerDetails.css';
import OpenOrderDetails from './OpenOrderDetails';
import OpenOrdersAnalysis from './OpenOrdersAnalysis';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function OpenPickerList({ warehouse, country, orderType, excludeCarriers, onBack }) {
  console.log('OpenPickerList received props:', { warehouse, country, orderType, excludeCarriers });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'oldest_days_open', direction: 'desc' });
  const [selectedPicker, setSelectedPicker] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // Local filters
  const [localOrderType, setLocalOrderType] = useState('All');
  const [localPicker, setLocalPicker] = useState('All');
  const [localExcludeCarriers, setLocalExcludeCarriers] = useState(excludeCarriers || false);
  const [availableOrderTypes, setAvailableOrderTypes] = useState([]);

  // Map order type codes to readable labels
  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  useEffect(() => {
    fetchPickerSummary();

    const refreshInterval = setInterval(() => {
      fetchPickerSummary();
    }, 60000); // 60 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [warehouse, country, orderType, localOrderType, localExcludeCarriers]);

  // Local order type wins over the one passed in from the dashboard
  const effectiveOrderType = localOrderType !== 'All' ? localOrderType : orderType;

  const fetchPickerSummary = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (country && country !== 'All') params.append('country', country);
      if (effectiveOrderType && effectiveOrderType !== 'All') params.append('orderType', effectiveOrderType);
      if (localExcludeCarriers) params.append('excludeCarriers', 'true');

      const response = await axios.get(`${API_URL}/open-orders/picker-summary/${encodeURIComponent(warehouse)}?${params}`);
      setData(response.data);
      setError(null);

      // Only grow the order type list so picking a type doesn't hide the others
      if (response.data.pickers) {
        setAvailableOrderTypes(prev => [...new Set([
          ...prev,
          ...response.data.pickers.flatMap(p =>
            p.orderTypes ? p.orderTypes.split(',').filter(Boolean) : []
          )
        ])].sort());
      }
    } catch (err) {
      console.error('Error fetching picker summary:', err);
      setError(`Failed to load picker summary: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handlePickerClick = (pickerName) => {
    setSelectedPicker(pickerName);
  };

  const handleBackToPickerList = () => {
    setSelectedPicker(null);
    // Refetch data when returning from order details
    fetchPickerSummary();
  };

  // Apply local filters (order type is filtered server-side)
  const filteredPickers = data?.pickers?.filter(picker => {
    if (localPicker !== 'All' && picker.picker_name !== localPicker) return false;
    return true;
  }) || [];

  const getSortedPickers = () => {
    const sorted = [...filteredPickers].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⬍';
    return sortConfig.direction === 'desc' ? ' ▼' : ' ▲';
  };

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  const formatWeight = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' lbs';
  };

  const formatCurrency = (num) => {
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  if (selectedPicker) {
    return (
      <OpenOrderDetails
        warehouse={warehouse}
        picker={selectedPicker}
        country={country}
        orderType={effectiveOrderType}
        excludeCarriers={localExcludeCarriers}
        onBack={handleBackToPickerList}
      />
    );
  }

  if (showAnalysis) {
    return (
      <OpenOrdersAnalysis
        warehouse={warehouse}
        country={country}
        orderType={effectiveOrderType}
        excludeCarriers={localExcludeCarriers}
        onBack={() => { setShowAnalysis(false); fetchPickerSummary(); }}
      />
    );
  }

  if (loading) {
    return <div className="picker-details-loading">Loading picker summary...</div>;
  }

  if (error) {
    return <div className="picker-details-error">{error}</div>;
  }

  const sortedPickers = getSortedPickers();

  // Calculate filtered totals
  const filteredTotals = {
    picks: filteredPickers.reduce((sum, p) => sum + (p.picks || 0), 0),
    lines: filteredPickers.reduce((sum, p) => sum + (p.lines || 0), 0),
    quantity: filteredPickers.reduce((sum, p) => sum + (p.quantity || 0), 0),
    weight: filteredPickers.reduce((sum, p) => sum + (p.weight || 0), 0),
    dollars: filteredPickers.reduce((sum, p) => sum + (p.dollars || 0), 0),
    totalDaysOpen: filteredPickers.reduce((sum, p) => sum + (p.total_days_to_pick || 0), 0)
  };
  // Weighted by picks so it matches the per-order average on the detail pages
  filteredTotals.avgDaysOpen = filteredTotals.picks > 0 ? filteredTotals.totalDaysOpen / filteredTotals.picks : 0;

  return (
    <div className="picker-details">
      <div className="picker-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Summary
        </button>
        <h3>{warehouse} - Picker Performance</h3>
        <p className="total-picks">Total Open Picks: {filteredTotals.picks}</p>
        <p className="total-picks">Avg Days Open: {filteredTotals.avgDaysOpen.toFixed(2)}</p>
      </div>

      <div className="filters-section" style={{ marginBottom: '20px' }}>
        <div className="filter-group">
          <label>Order Type:</label>
          <select value={localOrderType} onChange={(e) => setLocalOrderType(e.target.value)}>
            <option value="All">All</option>
            {availableOrderTypes.map(type => (
              <option key={type} value={type}>
                {orderTypeLabels[type] || type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Picker:</label>
          <select value={localPicker} onChange={(e) => setLocalPicker(e.target.value)}>
            <option value="All">All</option>
            {data?.pickers?.map(p => (
              <option key={p.picker_name} value={p.picker_name}>{p.picker_name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={localExcludeCarriers}
              onChange={(e) => setLocalExcludeCarriers(e.target.checked)}
            />
            Exclude Carriers: Z/WC/HFSK
          </label>
        </div>

        <button className="analyze-all-btn" onClick={() => setShowAnalysis(true)}>
          Analyze All Open Orders
        </button>
      </div>

      <div className="picker-table-container">
        <table className="picker-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('picker_name')}>
                Picker Name{getSortIndicator('picker_name')}
              </th>
              <th onClick={() => handleSort('oldest_days_open')}>
                Oldest Ticket Days Open{getSortIndicator('oldest_days_open')}
              </th>
              <th onClick={() => handleSort('avg_days_open')}>
                Avg Days to Pick{getSortIndicator('avg_days_open')}
              </th>
              <th onClick={() => handleSort('picks')}>
                Picks{getSortIndicator('picks')}
              </th>
              <th onClick={() => handleSort('lines')}>
                Lines{getSortIndicator('lines')}
              </th>
              <th onClick={() => handleSort('quantity')}>
                Quantity{getSortIndicator('quantity')}
              </th>
              <th onClick={() => handleSort('weight')}>
                Weight{getSortIndicator('weight')}
              </th>
              <th onClick={() => handleSort('dollars')}>
                Dollars{getSortIndicator('dollars')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPickers.map((picker) => (
              <tr key={picker.picker_name}>
                <td>
                  <span
                    className="picker-name clickable"
                    onClick={() => handlePickerClick(picker.picker_name)}
                  >
                    {picker.picker_name}
                  </span>
                </td>
                <td>{formatNumber(picker.oldest_days_open || 0)}</td>
                <td>{picker.avg_days_open?.toFixed(2) || '0.00'}</td>
                <td>{formatNumber(picker.picks)}</td>
                <td>{formatNumber(picker.lines)}</td>
                <td>{formatNumber(picker.quantity)}</td>
                <td>{formatWeight(picker.weight)}</td>
                <td>{formatCurrency(picker.dollars)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OpenPickerList;
